<template>
    <BaseDialog v-model:open="isOpen" title="Upload your team">
        <div class="flex flex-col gap-4">
            <!-- Step 1: Upload -->
            <template v-if="step === 'upload'">
                <p class="body-small text-ink-muted">
                    Upload a CSV of the team members' work emails. Only the <strong>email</strong> column is required. The team's lead sees the members' assessment results — that's
                    you, unless you designate someone else in the next step.
                </p>

                <!-- Mobile: simple upload button -->
                <div v-if="!selectedFile" class="md:hidden">
                    <BaseButton theme="neutral" class="w-full flex items-center justify-center gap-2" @click="triggerFileInput">
                        <i class="bi bi-file-earmark-arrow-up text-lg" aria-hidden="true"></i>
                        Upload CSV file
                    </BaseButton>
                </div>
                <!-- Desktop: drag-and-drop zone -->
                <div v-if="!selectedFile" class="hidden md:block">
                    <BaseCard
                        as="button"
                        type="button"
                        padding="lg"
                        :theme="isDragging ? 'muted' : 'default'"
                        :class="isDragging ? 'text-ink' : 'text-ink-muted'"
                        class="w-full border-dashed text-center"
                        @click="triggerFileInput"
                        @dragover.prevent="isDragging = true"
                        @dragleave="isDragging = false"
                        @drop.prevent="handleDrop">
                        <i class="bi bi-cloud-arrow-up text-3xl mb-2 block" aria-hidden="true"></i>
                        <span class="block body-small">Click to browse or drag and drop a CSV file</span>
                        <span class="block details mt-1">CSV files only, up to 1 MB</span>
                    </BaseCard>
                </div>

                <!-- File info card -->
                <div v-if="selectedFile" class="flex flex-col">
                    <BaseCard padding="sm" class="flex items-center gap-3">
                        <BaseChip size="2xs">CSV</BaseChip>
                        <div class="grow min-w-0">
                            <div class="body-small-semibold truncate">{{ selectedFile.name }}</div>
                        </div>
                        <BaseButton theme="neutral" size="sm" icon-only aria-label="Remove selected file" @click="clearFile">
                            <i class="bi bi-x" aria-hidden="true"></i>
                        </BaseButton>
                    </BaseCard>
                </div>

                <input ref="fileInput" type="file" accept=".csv" class="hidden" aria-label="CSV file upload" @change="handleFileSelect" />

                <BaseNotification v-if="uploadError" theme="error" hide-dismiss :text="uploadError" />

                <BaseButton theme="tertiary" class="inline-flex items-center gap-1 w-fit" @click="downloadTemplate">
                    <i class="bi bi-download" aria-hidden="true"></i>
                    Download CSV template
                </BaseButton>
            </template>

            <!-- Step 2: Preview -->
            <template v-else-if="step === 'preview' && previewData">
                <div>
                    <h3 class="body-bold text-ink">Review your team</h3>
                    <p class="body-small text-ink-muted mt-1">Emails that don't match a user in your organization will be skipped.</p>
                </div>

                <label for="team-name-input" class="flex flex-col gap-1">
                    <span class="body-small-semibold text-ink">Team name</span>
                    <BaseInput id="team-name-input" v-model="teamName" type="text" maxlength="255" placeholder="e.g. Platform Engineering" />
                </label>

                <label for="lead-email-input" class="flex flex-col gap-1">
                    <span class="body-small-semibold text-ink">Team lead's email</span>
                    <BaseInput id="lead-email-input" v-model="leadEmail" type="email" maxlength="254" placeholder="Leave empty if that's you" />
                    <span class="details text-ink-muted"
                        >Only the lead can see the team's assessment results. If you designate someone else, you'll manage the team without seeing results.</span
                    >
                </label>

                <div class="flex flex-wrap gap-3 body-small text-ink-muted">
                    <BaseChip size="xs">{{ previewData.summary.resolved }} will be added</BaseChip>
                    <BaseChip v-if="previewData.summary.unresolved > 0" size="xs">{{ previewData.summary.unresolved }} not found</BaseChip>
                    <BaseChip v-if="previewData.summary.errors > 0" size="xs">{{ previewData.summary.errors }} errors</BaseChip>
                </div>

                <div class="max-h-80 overflow-y-auto border border-border-strong rounded-lg">
                    <table class="w-full body-small">
                        <thead class="bg-surface-sunken sticky top-0">
                            <tr>
                                <th class="text-left p-2 font-medium text-ink-muted w-8">#</th>
                                <th class="text-left p-2 font-medium">Email</th>
                                <th class="text-left p-2 font-medium">Name</th>
                                <th class="text-left p-2 font-medium">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="row in previewData.rows" :key="row.row_index" :class="{ 'bg-notification-error-bg': !row.resolved }">
                                <td class="p-2 text-ink-muted">{{ row.row_index + 1 }}</td>
                                <td class="p-2">{{ row.email || "(empty)" }}</td>
                                <td class="p-2 text-ink-muted">{{ row.user_name || row.name || "-" }}</td>
                                <td class="p-2">
                                    <span v-if="row.resolved" class="text-notification-success-text">Will be added</span>
                                    <span v-else class="text-notification-error-text">{{ row.errors?.[0] || "Will be skipped" }}</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </template>

            <!-- Step 3: Importing -->
            <template v-else-if="step === 'importing'">
                <div class="text-center py-10">
                    <div class="overflow-hidden mb-3"><i class="bi bi-arrow-repeat text-3xl text-ink-muted block animate-spin" aria-hidden="true"></i></div>
                    <h3 class="title-body mb-2">Importing...</h3>
                    <template v-if="importProgress">
                        <p class="body-small text-ink-muted mb-4">{{ importProgress.processed }} of {{ importProgress.total }} rows processed</p>
                        <BaseProgressBar :value="progressPercent" :aria-label="`${importProgress.processed} of ${importProgress.total} rows processed`" />
                    </template>
                </div>
            </template>

            <!-- Step 4: Result -->
            <template v-else-if="step === 'result' && importResult">
                <div class="text-center py-4">
                    <i class="bi bi-check-circle text-4xl text-notification-success-text mb-3 block" aria-hidden="true"></i>
                    <h3 class="title-body mb-2">Team updated</h3>
                    <div class="flex flex-wrap justify-center gap-3 body-small text-ink-muted mb-4">
                        <StatusChip v-if="importResult.created" state="done" size="xs">{{ importResult.created }} added</StatusChip>
                        <BaseChip v-if="importResult.skipped" size="xs">{{ importResult.skipped }} skipped</BaseChip>
                        <BaseChip v-if="importResult.errors?.length" size="xs">{{ importResult.errors?.length }} not found</BaseChip>
                    </div>
                    <details v-if="importResult.errors?.length" class="text-left mb-4 mx-auto max-w-md">
                        <summary class="body-small text-notification-error-text cursor-pointer">Show details</summary>
                        <ul class="mt-2 space-y-1 body-small text-ink-muted">
                            <li v-for="(rowError, idx) in importResult.errors ?? []" :key="idx" class="px-3 py-2 bg-notification-error-bg rounded text-notification-error-text">
                                {{ rowError.error }}
                            </li>
                        </ul>
                    </details>
                </div>
            </template>
        </div>
        <template v-if="step !== 'importing'" #footer>
            <template v-if="step === 'upload'">
                <BaseButton theme="tertiary" @click="isOpen = false">Cancel</BaseButton>
                <BaseButton theme="primary" :disabled="!selectedFile || isUploading" @click="handleUpload">{{ isUploading ? "Uploading..." : "Preview Import" }}</BaseButton>
            </template>
            <template v-else-if="step === 'preview' && previewData">
                <BaseButton theme="tertiary" @click="resetToUpload">Back</BaseButton>
                <BaseButton theme="primary" :disabled="isImporting || !hasImportableRows" @click="handleImport">{{ isImporting ? "Importing..." : "Confirm Import" }}</BaseButton>
            </template>
            <BaseButton v-else-if="step === 'result' && importResult" theme="primary" @click="handleDone">Done</BaseButton>
        </template>
    </BaseDialog>
</template>

<script setup lang="ts">
import { useIntervalFn } from "@vueuse/core";
import { fetchApi, useFetch } from "~vue/api";
import type { CsvImportStatusResponse, TeamCsvPreviewResponse } from "~vue/api/generated/teams-csv";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseChip from "~vue/components/BaseChip.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseProgressBar from "~vue/components/BaseProgressBar.vue";
import BaseInput from "~vue/components/form/BaseInput.vue";
import StatusChip from "~vue/components/StatusChip.vue";
import { computed, ref, watch } from "vue";

const TEMPLATE_URL = "/v1/teams/csv/template";
const PREVIEW_URL = "/v1/teams/csv/preview";
const IMPORT_URL = "/v1/teams/csv/import";
const POLL_INTERVAL_MS = 1000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;
const MAX_FILE_SIZE = 1 * 1024 * 1024; // 1MB

const props = defineProps<{ initialTeamName?: string }>();
const emit = defineEmits<{ imported: [CsvImportStatusResponse] }>();
const isOpen = defineModel<boolean>("open", { default: false });

const step = ref<"upload" | "preview" | "importing" | "result">("upload");
const selectedFile = ref<File | null>(null);
const isDragging = ref(false);
const uploadError = ref("");
const fileInput = ref<HTMLInputElement | null>(null);
const previewData = ref<TeamCsvPreviewResponse | null>(null);
const teamName = ref(props.initialTeamName ?? "");
const leadEmail = ref("");
const importResult = ref<CsvImportStatusResponse | null>(null);
const isUploading = ref(false);
const isImporting = ref(false);
const importProgress = ref<{ processed: number; total: number } | null>(null);
const pollStatusUrl = ref<string | null>(null);
const pollStartTime = ref(0);

const hasImportableRows = computed(() => previewData.value?.rows.some((row) => row.resolved) ?? false);

const progressPercent = computed(() => {
    if (!importProgress.value?.total) {
        return 0;
    }
    return Math.round((importProgress.value.processed / importProgress.value.total) * 100);
});

const { pause: pausePoll, resume: resumePoll } = useIntervalFn(
    async () => {
        if (!pollStatusUrl.value) {
            return;
        }
        if (Date.now() - pollStartTime.value > POLL_TIMEOUT_MS) {
            pausePoll();
            uploadError.value = "Import timed out. Please try again.";
            step.value = "preview";
            return;
        }
        const { data } = await useFetch(pollStatusUrl.value).get().json<CsvImportStatusResponse>();
        if (!data.value) {
            return;
        }
        importProgress.value = { processed: data.value.processed ?? 0, total: data.value.total ?? 0 };
        if (data.value.status === "completed") {
            pausePoll();
            importResult.value = data.value;
            step.value = "result";
        } else if (data.value.status === "failed") {
            pausePoll();
            uploadError.value = data.value.error_message || "Import failed. Please try again.";
            step.value = "preview";
        }
    },
    POLL_INTERVAL_MS,
    { immediate: false },
);

const triggerFileInput = () => {
    fileInput.value?.click();
};

const downloadTemplate = async () => {
    const response = await fetchApi(TEMPLATE_URL);
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "team_members_template.csv";
    a.click();
    URL.revokeObjectURL(url);
};

const setFile = (file: File) => {
    if (file.size > MAX_FILE_SIZE) {
        uploadError.value = "File size exceeds 1 MB limit.";
        return;
    }
    selectedFile.value = file;
    uploadError.value = "";
};

const handleFileSelect = (event: Event) => {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
        setFile(file);
    }
};

const handleDrop = (event: DragEvent) => {
    isDragging.value = false;
    const file = event.dataTransfer?.files?.[0];
    if (file && file.name.endsWith(".csv")) {
        setFile(file);
    } else {
        uploadError.value = "Please drop a CSV file.";
    }
};

const clearFile = () => {
    selectedFile.value = null;
    uploadError.value = "";
    if (fileInput.value) {
        fileInput.value.value = "";
    }
};

const handleUpload = async () => {
    if (!selectedFile.value) {
        return;
    }
    isUploading.value = true;
    uploadError.value = "";

    const formData = new FormData();
    formData.append("file", selectedFile.value);

    const { data, error } = await useFetch(PREVIEW_URL).post(formData).json<TeamCsvPreviewResponse>();
    isUploading.value = false;

    if (error.value || !data.value) {
        uploadError.value = error.value?.message || "Failed to process CSV file. Please try again.";
        return;
    }
    previewData.value = data.value;
    step.value = "preview";
};

const handleImport = async () => {
    if (!previewData.value) {
        return;
    }
    isImporting.value = true;

    const rows = previewData.value.rows.filter((row) => row.resolved).map((row) => ({ row_index: row.row_index, email: row.email, name: row.name, skip: false }));

    const { data, error } = await useFetch(IMPORT_URL).post({ team_name: teamName.value, lead_email: leadEmail.value.trim(), rows }).json<{ status_url: string }>();
    isImporting.value = false;

    if (error.value || !data.value?.status_url) {
        uploadError.value = error.value?.message || "Import failed. Please try again.";
        return;
    }

    pollStatusUrl.value = data.value.status_url;
    importProgress.value = { processed: 0, total: rows.length };
    pollStartTime.value = Date.now();
    step.value = "importing";
    resumePoll();
};

const resetToUpload = () => {
    step.value = "upload";
    previewData.value = null;
};

const handleDone = () => {
    isOpen.value = false;
    if (importResult.value) {
        emit("imported", importResult.value);
    }
};

watch(isOpen, (newVal) => {
    if (!newVal) {
        pausePoll();
        step.value = "upload";
        selectedFile.value = null;
        uploadError.value = "";
        previewData.value = null;
        leadEmail.value = "";
        importResult.value = null;
        isUploading.value = false;
        isImporting.value = false;
        isDragging.value = false;
        importProgress.value = null;
        pollStatusUrl.value = null;
    }
});
</script>
